import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface AuditLog {
  id: number;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  action_type: string;
  resource_type: string;
  resource_id: number;
  project_name: string;
  details: any;
  created_at: string;
}

interface ExportLog {
  id: number;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  project_name: string;
  export_type: string;
  row_count: number;
  columns_exported: string[];
  filters_applied: any;
  file_name: string;
  created_at: string;
}

interface AuditStats {
  actionBreakdown: Array<{ action_type: string; count: string }>;
  recentActivityCount: number;
  topUsers: Array<{
    email: string;
    first_name: string;
    last_name: string;
    action_count: string;
  }>;
  exportStats: Array<{
    export_type: string;
    export_count: string;
    total_rows_exported: string;
  }>;
}

const AuditLogs: React.FC = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState<'all' | 'exports' | 'stats'>('all');
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [exportLogs, setExportLogs] = useState<ExportLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    if (user?.teamId && user?.teamRole === 'admin') {
      loadData();
    }
  }, [user, activeTab, page]);

  const loadData = async () => {
    if (!user?.teamId || !token) return;

    setLoading(true);
    setError('');

    try {
      if (activeTab === 'all') {
        const response = await fetch(
          `${API_BASE}/api/teams/${user.teamId}/audit-logs?limit=${limit}&offset=${(page - 1) * limit}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAuditLogs(data.logs || []);
          setTotal(data.total || 0);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Audit logs error:', response.status, errorData);
          setError(`Failed to load audit logs: ${errorData.error || response.statusText}`);
        }
      } else if (activeTab === 'exports') {
        const response = await fetch(
          `${API_BASE}/api/teams/${user.teamId}/export-logs?limit=${limit}&offset=${(page - 1) * limit}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setExportLogs(data.exports || []);
          setTotal(data.total || 0);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Export logs error:', response.status, errorData);
          setError(`Failed to load export logs: ${errorData.error || response.statusText}`);
        }
      } else if (activeTab === 'stats') {
        const response = await fetch(
          `${API_BASE}/api/teams/${user.teamId}/audit-stats`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setStats(data);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Audit stats error:', response.status, errorData);
          setError(`Failed to load statistics: ${errorData.error || response.statusText}`);
        }
      }
    } catch (err: any) {
      console.error('Audit logs fetch error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatActionType = (actionType: string) => {
    return actionType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!user?.teamId) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">You must be part of a team to view audit logs.</p>
      </div>
    );
  }

  if (user?.teamRole !== 'admin') {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Only team admins can view audit logs.</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">Audit Logs & Activity</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setActiveTab('all');
              setPage(1);
            }}
            className={`pb-3 px-4 border-b-2 font-medium ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            All Activity
          </button>
          <button
            onClick={() => {
              setActiveTab('exports');
              setPage(1);
            }}
            className={`pb-3 px-4 border-b-2 font-medium ${
              activeTab === 'exports'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Exports
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`pb-3 px-4 border-b-2 font-medium ${
              activeTab === 'stats'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Statistics
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading...</p>
        </div>
      ) : (
        <>
          {/* All Activity Tab */}
          {activeTab === 'all' && (
            <div>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {log.user_first_name} {log.user_last_name}
                          </div>
                          <div className="text-sm text-gray-500">{log.user_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {formatActionType(log.action_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.resource_type || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.project_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {log.details && Object.keys(log.details).length > 0 ? (
                            <details className="cursor-pointer">
                              <summary>View</summary>
                              <pre className="mt-2 text-xs bg-gray-50 p-2 rounded">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </details>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page * limit >= total}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Exports Tab */}
          {activeTab === 'exports' && (
            <div>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rows</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Columns</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {exportLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {log.user_first_name} {log.user_last_name}
                          </div>
                          <div className="text-sm text-gray-500">{log.user_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {log.export_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.row_count.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.columns_exported.length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.project_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {log.file_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page * limit >= total}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Tab */}
          {activeTab === 'stats' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Recent Activity (Last 7 Days)</h3>
                <p className="text-4xl font-bold text-blue-600">{stats.recentActivityCount}</p>
                <p className="text-gray-600 mt-2">Total actions</p>
              </div>

              {/* Action Breakdown */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Action Breakdown</h3>
                <div className="space-y-2">
                  {stats.actionBreakdown.map((action) => (
                    <div key={action.action_type} className="flex justify-between items-center">
                      <span className="text-gray-700">{formatActionType(action.action_type)}</span>
                      <span className="font-semibold">{action.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Users */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Most Active Users</h3>
                <div className="space-y-3">
                  {stats.topUsers.map((user) => (
                    <div key={user.email} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-lg font-semibold text-blue-600">{user.action_count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Export Statistics */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Export Statistics</h3>
                <div className="space-y-3">
                  {stats.exportStats.map((stat) => (
                    <div key={stat.export_type} className="border-b pb-2">
                      <p className="font-medium">{stat.export_type}</p>
                      <div className="flex justify-between text-sm text-gray-600 mt-1">
                        <span>{stat.export_count} exports</span>
                        <span>{parseInt(stat.total_rows_exported).toLocaleString()} total rows</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogs;
